import React, { useState } from 'react';
import {
  TECH_DOMAINS,
  TIER_LABEL,
  CATEGORY_LABEL,
  type TechTier,
  type TechDomain,
} from '../../data/techDomains';
import { TechWikiModal } from './TechWikiModal';
import './TechnicalKnowledge.css';
import './TechWikiModal.css';

const TIER_ORDER: TechTier[] = ['core', 'proficient', 'familiar'];

export const TechnicalKnowledge: React.FC<{ topSlot?: React.ReactNode }> = ({ topSlot }) => {
  const [activeDomain, setActiveDomain] = useState<TechDomain | null>(null);

  // 按层级分组
  const grouped = TIER_ORDER.map((tier) => ({
    tier,
    label: TIER_LABEL[tier],
    domains: TECH_DOMAINS.filter((d) => d.tier === tier),
  })).filter((g) => g.domains.length > 0);

  return (
    <section className="tk-section-wrap" data-testid="section-tech-knowledge">
      <div className="tk-inner">
        {/* Header */}
        <header className="tk-header">
          <div className="tk-eyebrow">KNOWLEDGE GRAPH</div>
          <h2 className="tk-title">能力图谱</h2>
          <p className="tk-subtitle">
            以云计算 × AI 双主线构建的技术版图 · 10 个领域按熟练度分层展示，一屏纵览
          </p>
          <div className="tk-hint">
            <i className="fa-solid fa-hand-pointer"></i>
            <span>点击任意卡片即可查看该领域详细知识树</span>
          </div>

          {/* 图例：熟练度层级 + 分类色 */}
          <div className="tk-legend">
            <div className="tk-legend-group tk-legend-tier">
              <span className="tk-legend-item" style={{ background: 'transparent', border: 'none', padding: 0, color: 'rgba(255,255,255,0.4)', fontSize: 10, letterSpacing: 1.5, marginRight: 2 }}>
                熟练度
              </span>
              <span className="tk-legend-item" style={{ background: 'transparent', border: 'none', padding: 0 }}>
                <span className="tk-legend-dot tk-legend-dot--core" /> 核心 85+
              </span>
              <span className="tk-legend-sep">·</span>
              <span className="tk-legend-item" style={{ background: 'transparent', border: 'none', padding: 0 }}>
                <span className="tk-legend-dot tk-legend-dot--proficient" /> 熟练 75-84
              </span>
              <span className="tk-legend-sep">·</span>
              <span className="tk-legend-item" style={{ background: 'transparent', border: 'none', padding: 0 }}>
                <span className="tk-legend-dot tk-legend-dot--familiar" /> 了解 &lt;75
              </span>
            </div>

            <div className="tk-legend-group tk-legend-cat">
              <span className="tk-legend-item" style={{ background: 'transparent', border: 'none', padding: 0, color: 'rgba(255,255,255,0.4)', fontSize: 10, letterSpacing: 1.5, marginRight: 2 }}>
                分类
              </span>
              <span className="tk-legend-item" style={{ background: 'transparent', border: 'none', padding: 0 }}>
                <span className="tk-legend-dot tk-legend-dot--ai" /> AI
              </span>
              <span className="tk-legend-sep">·</span>
              <span className="tk-legend-item" style={{ background: 'transparent', border: 'none', padding: 0 }}>
                <span className="tk-legend-dot tk-legend-dot--cloud" /> 云计算
              </span>
              <span className="tk-legend-sep">·</span>
              <span className="tk-legend-item" style={{ background: 'transparent', border: 'none', padding: 0 }}>
                <span className="tk-legend-dot tk-legend-dot--security" /> 安全
              </span>
            </div>
          </div>
        </header>

        {/* 顶部插槽：合并 section 场景下用于放能力总览雷达图 */}
        {topSlot && <div className="tk-top-slot">{topSlot}</div>}

        {/* 按层级分组渲染 */}
        {grouped.map((group) => (
          <div key={group.tier} className={`tk-group tk-group--${group.tier}`}>
            <div className="tk-group-head">
              <span className="tk-group-name">{group.label.name}</span>
              <span className="tk-group-range">{group.label.range}</span>
              <span className="tk-group-hint">{group.label.hint}</span>
            </div>

            <div className="tk-grid">
              {group.domains.map((d, idx) => {
                const clickable = !!d.mdFile;
                return (
                  <article
                    key={d.key}
                    className={`tk-domain-card tk-cat--${d.category} ${clickable ? 'tk-domain-card--clickable' : ''}`}
                    style={{ animationDelay: `${idx * 0.06}s` }}
                    onClick={clickable ? () => setActiveDomain(d) : undefined}
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onKeyDown={
                      clickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setActiveDomain(d);
                            }
                          }
                        : undefined
                    }
                  >
                    <div className="tk-domain-head">
                      <span className="tk-domain-icon">
                        <i className={`fa-solid ${d.icon}`} />
                      </span>
                      <div className="tk-domain-title-block">
                        <h3 className="tk-domain-name">{d.name}</h3>
                        <span className="tk-domain-cat">{CATEGORY_LABEL[d.category]}</span>
                      </div>
                      <div className="tk-domain-score">
                        <span className="tk-domain-score-num">{d.score}</span>
                        <span className="tk-domain-score-max">/100</span>
                      </div>
                    </div>

                    <div className="tk-progress">
                      <div
                        className="tk-progress-fill"
                        style={{ width: `${d.score}%` }}
                      />
                    </div>

                    <p className="tk-domain-summary">{d.summary}</p>

                    <div className="tk-domain-highlights">
                      {d.highlights.map((h) => (
                        <span key={h} className="tk-domain-chip">{h}</span>
                      ))}
                    </div>

                    {d.keywords && d.keywords.length > 0 && group.tier !== 'familiar' && (
                      <div className="tk-domain-keywords">
                        {d.keywords.map((k) => (
                          <span key={k} className="tk-domain-kw">{k}</span>
                        ))}
                      </div>
                    )}

                    {/* 深入了解按钮 —— 只对配置了 mdFile 的领域显示; 阻止冒泡避免与整卡点击重复触发 */}
                    {clickable && (
                      <button
                        className="tk-domain-more"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDomain(d);
                        }}
                        type="button"
                        tabIndex={-1}
                      >
                        <i className="fa-solid fa-book-open" />
                        深入了解
                        <i className="fa-solid fa-arrow-right" />
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Wiki 详情 Modal */}
      {activeDomain && (
        <TechWikiModal
          domain={activeDomain}
          onClose={() => setActiveDomain(null)}
        />
      )}
    </section>
  );
};

export default TechnicalKnowledge;
